import React, { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PlanPosition } from '../types'
import TableFooterLeftToolbarButtons from '../components/Buttons/TableFooterLeftToolbarButtons'
import TableFooterRightToolbarButtons from '../components/Buttons/TableFooterRightToolbarButtons'
import { OrganizationInfo } from '../../../Org/Home/types'

type Props = {
    user_guid: string | null
    regNumber: string | undefined
    position: PlanPosition
    approve: boolean
    isUserRoleName: string
    isSendPlan: boolean
    handleSave: () => void
    onPositionStatusChange: (posStatus: string) => void
    onPositionStageChange: (posStage: string) => void
    handleValidation: () => void
    isHaveError: boolean
    theme: string
    lastApprovalStatus: boolean
    lastApprovalStatusAccept: boolean
    organization?: OrganizationInfo
}

const Footer: React.FC<Props> = ({
    user_guid,
    regNumber,
    position,
    approve,
    isUserRoleName,
    isSendPlan,
    handleSave,
    onPositionStatusChange,
    onPositionStageChange,
    handleValidation,
    isHaveError,
    theme,
    lastApprovalStatus,
    lastApprovalStatusAccept,
    organization,
}) => {
    const navigate = useNavigate()
    const [isStatusChanging, setStatusChanging] = useState<boolean>(false)
    const [isStageChanging, setStageChanging] = useState<boolean>(false)

    const [changePlanPositionPopup, setChangePlanPositionPopup] =
        useState<boolean>(false)

    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deletePlanPositionPopup, setDeletePlanPositionPopup] =
        useState<boolean>(false)

    const [isCopying, setCopying] = useState<boolean>(false)
    const [copyPlanPositionPopup, setCopyPlanPositionPopup] =
        useState<boolean>(false)
    const [copyPlanPositionDraftPopup, setCopyPlanPositionDraftPopup] =
        useState<boolean>(false)

    const [isCreating, setCreating] = useState<boolean>(false)

    const [isAnnulating, setAnnulating] = useState<boolean>(false)
    const [annulatePlanPositionPopup, setAnnulatePlanPositionPopup] =
        useState<boolean>(false)
    const [annulateReason, setAnnulateReason] = useState('REJECT')

    const [isExporting, setExporting] = useState<boolean>(false)

    const stageChangePosition = async (
        guid: string,
        stagePos: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_positions_draft_change_stage/?reg_number=${regNumber}&pos_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ position_stage: stagePos }),
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to change stage position from. Status: ${response.status}`
                )
            }

            console.log('Position stage change successfully')
        } catch (error) {
            console.error('Error while changing stage position:', error)
            setStageChanging(false)
        } finally {
        }
    }

    const statusChangePosition = async (
        guid: string,
        statusPos: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_positions_draft_change_status/?reg_number=${regNumber}&pos_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ position_status: statusPos }),
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to change status position from. Status: ${response.status}`
                )
            }

            console.log('Position status change successfully')
            console.log(lastApprovalStatus)
        } catch (error) {
            console.error('Error while changing status position:', error)
            setStatusChanging(false)
        } finally {
        }
    }

    const copyPositionDraft = async (guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_position_draft_from_draft/?reg_number=${regNumber}&from_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }

            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setCopying(false)
        } finally {
        }
    }

    const copyPosition = async (guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_position_draft_new_from/?reg_number=${regNumber}&from_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }

            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setCopying(false)
        } finally {
        }
    }

    const changePosition = async (guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_position_draft_from/?reg_number=${regNumber}&from_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }
            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setCreating(false)
        } finally {
        }
    }

    const annulatePosition = async (
        guid: string,
        cancellationReason: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_position_draft_annulate/?reg_number=${regNumber}&guid=${guid}&cancellation_reason=${cancellationReason}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to annulate position from. Status: ${response.status}`
                )
            }

            console.log('Position annulate successfully')
        } catch (error) {
            console.error('Error while annulating position:', error)
            setAnnulating(false)
        } finally {
        }
    }

    const deletePosition = async (guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/plan_position_draft/?reg_number=${regNumber}&guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to delete position. Status: ${response.status}`
                )
            }

            console.log('Position deleted successfully')
        } catch (error) {
            console.error('Error while deleting position:', error)
            setDeleting(false)
        } finally {
        }
    }

    const handlePositionsExportExcel = async () => {
        const prefixUrl =
            position.position_stage == 'План закупки в ЕИС'
                ? 'export_positions_to_file'
                : 'export_positions_draft_to_file'

        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v2/${prefixUrl}?reg_number=${regNumber}`

        setExporting(true) // Устанавливаем состояние загрузки
        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    pos_guid: [`${position.guid}`],
                }),
            })

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`)
            }

            // Получение имени файла из заголовков
            const disposition = response.headers.get('Content-Disposition')
            const filename =
                disposition?.match(/filename="?(.+)"?/)?.[1] ||
                'Позиции плана закупок.xlsx'

            // Чтение файла и создание ссылки для скачивания
            const blob = await response.blob()
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filename
            link.click()
            window.URL.revokeObjectURL(link.href) // Очистка URL
        } catch (error) {
            console.error('Error exporting file:', error)
            alert('Ошибка экспорта файла.')
        } finally {
            setExporting(false) // Завершаем состояние загрузки
        }
    }

    const handleStageChange = (pos_guid: string, stage: string) => {
        stageChangePosition(pos_guid, stage)
        onPositionStageChange(stage)
        // handleValidation()
    }

    const handleStatusChange = (pos_guid: string, status: string) => {
        if (status == 'Готово') {
            handleSave()
        }
        statusChangePosition(pos_guid, status)
        onPositionStatusChange(status)
    }

    const handleCopyPlanPositionPopup = () => {
        setCopyPlanPositionPopup(!copyPlanPositionPopup)
    }

    const handleCopy = (pos_guid: string) => {
        copyPosition(pos_guid)
        navigate(`/hub/plans/${regNumber}/positions?stage=Редактирование`)
    }

    const handleCopyPlanPositionDraftPopup = () => {
        setCopyPlanPositionDraftPopup(!copyPlanPositionDraftPopup)
    }

    const handleCopyDraft = (pos_guid: string) => {
        handleSave()
        copyPositionDraft(pos_guid)
        navigate(`/hub/plans/${regNumber}/positions?stage=Редактирование`)
    }

    const handleChangePlanPositionPopup = () => {
        setChangePlanPositionPopup(!changePlanPositionPopup)
    }

    const handleChange = async (pos_guid: string) => {
        changePosition(pos_guid)
        navigate(`/hub/plans/${regNumber}/positions?stage=Редактирование`)
    }

    const handleAnnulatePlanPositionPopup = () => {
        setAnnulatePlanPositionPopup(!annulatePlanPositionPopup)
    }

    const handleAnnulateReasonChange = async (value: string) => {
        setAnnulateReason(value)
    }

    const handleAnnulate = async (
        pos_guid: string,
        cancellationReason: string
    ) => {
        annulatePosition(pos_guid, cancellationReason)
        navigate(
            `/hub/plans/${regNumber}/positions?stage=Подготовка к размещению`
        )
    }

    const handleDeletePlanPositionPopup = () => {
        setDeletePlanPositionPopup(!deletePlanPositionPopup)
    }

    const handleDelete = (
        pos_guid: string,
        stage = position.position_stage
    ) => {
        deletePosition(pos_guid)
        navigate(`/hub/plans/${regNumber}/positions?stage=${stage}`)
    }

    const handleSaveClose = () => {
        handleSave()
        navigate(
            `/hub/plans/${regNumber}/positions?stage=${position.position_stage}`
        )
    }

    const handleClose = () => {
        navigate(
            `/hub/plans/${regNumber}/positions?stage=${position.position_stage}`
        )
    }

    return (
        <footer className="flex flex-row w-full min-h-10 items-center justify-end ">
            <div className="flex flex-row gap-2 p-2 px-[22px] w-full h-full bg-[#d2d5d8] dark:bg-[#262427] items-center justify-start">
                <TableFooterLeftToolbarButtons
                    user_guid={user_guid}
                    pos_user_guid={position.user_guid}
                    pos_supervisor_user_guid={position.supervisor_user_guid}
                    role={isUserRoleName}
                    stage={position.position_stage}
                    status={position.position_status}
                    approve={approve}
                    isSendPlan={isSendPlan}
                    isHaveError={isHaveError}
                    theme={theme}
                    lastApprovalStatus={lastApprovalStatus}
                    lastApprovalStatusAccept={lastApprovalStatusAccept}
                    organizationApproval={organization?.active_approval}
                    actions={{
                        return_to_edit: () => {
                            handleStageChange(position.guid, 'Редактирование')
                            handleStatusChange(position.guid, 'Черновик')
                        },
                        return_to_ready: () => {
                            handleStageChange(
                                position.guid,
                                'Подготовка к размещению'
                            )
                            handleStatusChange(position.guid, 'Подготовка')
                        },
                        ready: () =>
                            handleStatusChange(position.guid, 'Готово'),
                        edit: () =>
                            handleStatusChange(position.guid, 'Черновик'),
                        edit_ready: () =>
                            handleStatusChange(position.guid, 'Подготовка'),
                        ready_to_sent: () => {
                            handleStageChange(
                                position.guid,
                                'Подготовка к размещению'
                            )
                            handleStatusChange(position.guid, 'Подготовка')
                        },
                        ready_to_eis: () => {
                            handleStageChange(position.guid, 'Отправка в ЕИС')
                            handleStatusChange(
                                position.guid,
                                'Готово к отправке'
                            )
                        },
                        validation: handleValidation,
                    }}
                />
                <div className="flex flex-1"></div>
                <TableFooterRightToolbarButtons
                    user_guid={user_guid}
                    pos_user_guid={position.user_guid}
                    pos_supervisor_user_guid={position.supervisor_user_guid}
                    role={isUserRoleName}
                    stage={position.position_stage}
                    status={position.position_status}
                    approve={approve}
                    isSendPlan={isSendPlan}
                    isAnnulate={position.status == 'A'}
                    theme={theme}
                    lastApprovalStatus={lastApprovalStatus}
                    lastApprovalStatusAccept={lastApprovalStatusAccept}
                    actions={{
                        save: handleSave,
                        copy: handleCopyPlanPositionPopup,
                        copy_draft: handleCopyPlanPositionDraftPopup,
                        delete: handleDeletePlanPositionPopup,
                        close_save: handleSaveClose,
                        close: handleClose,
                        close_approve: () => navigate(`/hub/approvals/plans`),
                        change: handleChangePlanPositionPopup,
                        annulate: handleAnnulatePlanPositionPopup,
                        export_positions_to_excel: handlePositionsExportExcel,
                    }}
                />
            </div>

            {copyPlanPositionPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleCopyPlanPositionPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Копировать позицию?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleCopyPlanPositionPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() => handleCopy(position.guid)}
                                >
                                    Копировать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {copyPlanPositionDraftPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleCopyPlanPositionDraftPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Копировать позицию?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleCopyPlanPositionDraftPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() =>
                                        handleCopyDraft(position.guid)
                                    }
                                >
                                    Копировать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {changePlanPositionPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleChangePlanPositionPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Изменить позицию?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleChangePlanPositionPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() => handleChange(position.guid)}
                                >
                                    Изменить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {deletePlanPositionPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleDeletePlanPositionPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить позицию?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleDeletePlanPositionPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() =>
                                        handleDelete(
                                            position.guid,
                                            position.position_stage
                                        )
                                    }
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {annulatePlanPositionPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleAnnulatePlanPositionPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Обоснование аннулирования позиций
                                </label>
                                <select
                                    className="form-select flex p-2 rounded-md border-0 text-sm bg-[#181818] text-[#181818] dark:text-[#FFFFFF]"
                                    value={annulateReason}
                                    onChange={(e) =>
                                        handleAnnulateReasonChange(
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="REJECT">
                                        Отказ от проведения закупки
                                    </option>
                                    <option value="INVALID">
                                        Признание торгов недействительными
                                    </option>
                                    <option value="NOT_PERFORMED">
                                        Торги не состоялись
                                    </option>
                                </select>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleAnnulatePlanPositionPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() =>
                                        handleAnnulate(
                                            position.guid,
                                            annulateReason
                                        )
                                    }
                                >
                                    Аннулировать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </footer>
    )
}

export default Footer
